/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2022] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import AWPackageStatuses from '@/enums/AWPackageStatuses';
import { userHasRole } from '@/util/RoleUtils';

const {
  DRAFT,
  HIDDEN,
  PENDING_ACCEPTANCE,
  PENDING_ORDER_CREATION,
  SENT_TO_CONTRACTING,
  ORDER_ISSUED,
  RETURNED,
  RESUBMITTED_TO_CONTRACTING,
  SUBMITTING_TO_CONTRACTING,
  SUBMISSION_ERROR,
  ARCHIVED
} = AWPackageStatuses;

//ACCESS LOGIC AGREED UPON WITH JON & MEGHAN 9/30/2022
//REVISED WITH MEGHAN AND ED 5/30/24

const AWPackageAccess = {
  isEditable(isEPCOwner, isEditing, statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');

    if (statusId === ARCHIVED) {
      return false
    }

    if (isAdmin) {
      return true;
    }

    switch (statusId) {
      case SENT_TO_CONTRACTING:
        return false;
      case SUBMITTING_TO_CONTRACTING:
        return false;
      case RESUBMITTED_TO_CONTRACTING:
        return false;
      case ORDER_ISSUED:
        return false;
      case HIDDEN:
      case ARCHIVED:
        return false;
      default:
        return isEPCOwner && isEditing;
    }
  },

  isNotesEditable(statusId) {
    if (EXTERNAL) return false;
    const isEPC = userHasRole('EPC');
    const isAdmin = userHasRole('Admin');
    switch (statusId) {
      case SENT_TO_CONTRACTING:
        return false;
      case SUBMITTING_TO_CONTRACTING:
        return false;
      case RESUBMITTED_TO_CONTRACTING:
        return false;
      case ORDER_ISSUED:
        return false;
      case HIDDEN:
      case ARCHIVED:
        return false;
      default:
        return isEPC || isAdmin;
    }
  },

  isSavable(isEPCOwner, isEditing, statusId) {
    return this.isEditable(isEPCOwner, isEditing, statusId);
  },

  isSubmittable(isEPCOwner, statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');

    switch (statusId) {
      case DRAFT:
      case PENDING_ACCEPTANCE:
      case PENDING_ORDER_CREATION:
      case ORDER_ISSUED:
      case RETURNED:
      case RESUBMITTED_TO_CONTRACTING:
      case SUBMISSION_ERROR:
        return isEPCOwner || isAdmin;
      default:
        return false;
    }
  },

  isSubmittableToAW(isEPCOwner, statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');

    switch (statusId) {
      case PENDING_ORDER_CREATION:
      case RETURNED:
      case SUBMISSION_ERROR:
        return isEPCOwner || isAdmin;
      default:
        return false;
    }
  },

  isEditButtonVisible(isEPCOwner, statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');
    const isDraft = statusId === AWPackageStatuses.DRAFT;
    const isSubmitting = statusId === SUBMITTING_TO_CONTRACTING;
    const isSent = statusId === SENT_TO_CONTRACTING;
    const isReturnedOrError = statusId === RETURNED || statusId === SUBMISSION_ERROR;
    const isPending = statusId === PENDING_ACCEPTANCE || statusId === PENDING_ORDER_CREATION;

    return (isEPCOwner || isAdmin) && !isDraft && (isReturnedOrError || isPending);
  },

  isReassignable(isEPCOwner, statusId) {
    return this.isSubmittable(isEPCOwner, statusId);
  },

  isDeleteable(statusId, isEPCOwner) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');

    switch (statusId) {
      case DRAFT:
        return isAdmin || isEPCOwner;
      default:
        return false;
    }
  },

  isHideButtonVisible(statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');
    if (isAdmin && ![HIDDEN, ARCHIVED].includes(statusId)) {
      return true;
    } else return false;
  },

  isFundingSourceEditable(statusId) {
    switch (statusId) {
      case DRAFT:
      case PENDING_ACCEPTANCE:
      case PENDING_ORDER_CREATION:
        return true;
      default:
        return false;
    }
  },

  isDeliveryScheduleColumnRenderable(renderableBy) {
    if (EXTERNAL) return false;
    if (renderableBy === 'All' || renderableBy === 'Internal') {
      return true;
    } else {
      return false;
    }
  },

  isDeliveryScheduleColumnDownloadable(downloadableBy) {
    if (EXTERNAL) return false;
    if (downloadableBy === 'All' || downloadableBy === 'Internal') {
      return true;
    } else {
      return false;
    }
  }
};

export default AWPackageAccess;
